import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale, Target, Activity, Utensils, Clock, ChevronDown } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';
import { ProteinGuideContent } from '../components/tools/ProteinGuideContent';
import { ProteinProtocolGate } from '../components/tools/ProteinProtocolGate';

const WEBAPP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "NinjAthlete Protein Intake Calculator",
  "url": "https://ninjathlete.com/tools/protein-calculator",
  "description": "Free science-backed protein calculator that gives your exact daily protein target in grams based on body weight, training goal, and activity level. Built on peer-reviewed research including Morton et al. 2018 and the ISSN Position Stand.",
  "applicationCategory": "HealthApplication",
  "operatingSystem": "Any",
  "browserRequirements": "Requires JavaScript",
  "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
  "publisher": {
    "@type": "Organization",
    "name": "NinjAthlete",
    "url": "https://ninjathlete.com",
    "logo": "https://ninjathlete.com/logo.png",
    "sameAs": ["https://instagram.com/ninjathlete"]
  }
};

const GOALS = [
  { value: 'maintain', label: 'Maintain Muscle', multiplier: 1.0 },
  { value: 'build', label: 'Build Muscle', multiplier: 1.2 },
  { value: 'lose_fat', label: 'Lose Fat', multiplier: 0.9 },
  { value: 'athletic', label: 'Athletic Performance', multiplier: 1.3 },
  { value: 'general', label: 'General Health', multiplier: 0.8 },
];

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary (little to no exercise)', factor: 0.8 },
  { value: 'light', label: 'Lightly Active (1-2x/week)', factor: 0.9 },
  { value: 'moderate', label: 'Moderate (3-4x/week)', factor: 1.0 },
  { value: 'active', label: 'Very Active (5-6x/week)', factor: 1.1 },
  { value: 'extreme', label: 'Extremely Active (daily intense)', factor: 1.2 },
];

const SelectDropdown = ({ label, icon: Icon, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#E63946]/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#E63946]" />}
          {selected?.label || 'Select...'}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl max-h-60 overflow-auto">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                opt.value === value ? 'bg-[#E63946]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ProteinCalculatorPage() {
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState('lbs');
  const [goal, setGoal] = useState('maintain');
  const [activity, setActivity] = useState('moderate');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'protein-webapp-jsonld';
    script.textContent = JSON.stringify(WEBAPP_SCHEMA);
    document.head.appendChild(script);
    let canonical = document.querySelector('link[rel="canonical"]');
    const created = !canonical;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://ninjathlete.com/tools/protein-calculator');
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
      if (created && canonical.parentNode) canonical.parentNode.removeChild(canonical);
    };
  }, []);

  const calculate = () => {
    if (!weight || isNaN(weight) || parseFloat(weight) <= 0) return;
    const weightKg = unit === 'lbs' ? parseFloat(weight) * 0.453592 : parseFloat(weight);
    const goalData = GOALS.find(g => g.value === goal);
    const activityData = ACTIVITY_LEVELS.find(a => a.value === activity);
    // Base: 1.6g/kg for maintenance, adjusted by goal and activity
    const baseProtein = 1.6;
    const dailyGrams = Math.round(weightKg * baseProtein * goalData.multiplier * activityData.factor);
    const meals = goal === 'build' || goal === 'athletic' ? 5 : 4;
    const perMeal = Math.round(dailyGrams / meals);
    const perKg = (dailyGrams / weightKg).toFixed(1);
    const perLb = (dailyGrams / (weightKg * 2.20462)).toFixed(1);
    const calories = dailyGrams * 4;
    // Timing suggestions
    const timing = [];
    if (meals >= 4) {
      timing.push({ meal: 'Breakfast', grams: perMeal, time: '7:00 AM', example: 'Eggs + Greek yogurt or protein shake' });
      timing.push({ meal: 'Lunch', grams: perMeal, time: '12:00 PM', example: 'Chicken breast, salmon, or lean beef' });
      timing.push({ meal: 'Post-Workout', grams: Math.round(perMeal * 1.2), time: '4:00 PM', example: 'Whey protein shake + banana' });
      timing.push({ meal: 'Dinner', grams: perMeal, time: '7:00 PM', example: 'Fish, turkey, or tofu with vegetables' });
    }
    if (meals === 5) {
      timing.splice(2, 0, { meal: 'Snack', grams: Math.round(perMeal * 0.6), time: '3:00 PM', example: 'Cottage cheese, nuts, or protein bar' });
    }
    setResult({ dailyGrams, meals, perMeal, perKg, perLb, calories, timing, weightKg });
    setSaved(false);
  };

  return (
    <ToolSEOWrapper toolKey="protein-calculator">
    <main className="pt-20 min-h-screen" data-testid="protein-calculator-page">
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8" data-testid="back-to-tools">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>

        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#E63946] text-white mb-4">Calculator</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="protein-calc-title">Protein Requirements Calculator</h1>
            <p className="text-zinc-400">Calculate your exact daily protein needs based on your goals</p>
          </div>

          {/* Calculator Card */}
          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Body Weight */}
              <div data-testid="weight-input-container">
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Body Weight</label>
                <div className="relative">
                  <Scale className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E63946]" />
                  <input
                    type="number"
                    value={weight}
                    onChange={e => setWeight(e.target.value)}
                    placeholder="Enter weight"
                    className="w-full pl-10 pr-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors"
                    data-testid="weight-input"
                  />
                </div>
              </div>
              {/* Unit */}
              <SelectDropdown
                label="Unit"
                icon={null}
                value={unit}
                options={[{ value: 'lbs', label: 'Pounds (lbs)' }, { value: 'kg', label: 'Kilograms (kg)' }]}
                onChange={setUnit}
                testId="unit-select"
              />
              {/* Goal */}
              <SelectDropdown
                label="Primary Goal"
                icon={Target}
                value={goal}
                options={GOALS}
                onChange={setGoal}
                testId="goal-select"
              />
            </div>
            {/* Activity Level */}
            <div className="mb-6">
              <SelectDropdown
                label="Activity Level"
                icon={Activity}
                value={activity}
                options={ACTIVITY_LEVELS}
                onChange={setActivity}
                testId="activity-select"
              />
            </div>
            {/* Calculate Button */}
            <button
              onClick={calculate}
              disabled={!weight}
              className="w-full py-4 bg-gradient-to-r from-[#E63946] to-[#ff6b35] text-white font-heading text-lg uppercase tracking-wider hover:from-[#d62f3c] hover:to-[#e5612e] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="calculate-protein-btn"
            >
              Calculate My Protein Needs
            </button>
          </div>

          {/* Results */}
          {result && (
            <div className="mt-8 space-y-6 animate-entrance" data-testid="protein-results">
              {/* Main Result */}
              <div className="bg-zinc-900/80 border border-[#E63946]/30 p-6 md:p-8 text-center">
                <p className="text-zinc-400 text-sm uppercase tracking-wider mb-2">Your Daily Protein Target</p>
                <div className="text-6xl md:text-7xl font-heading text-white mb-2" data-testid="protein-daily-grams">
                  {result.dailyGrams}<span className="text-2xl text-[#E63946]">g</span>
                </div>
                <p className="text-zinc-500 text-sm">per day &bull; {result.perKg}g/kg &bull; {result.perLb}g/lb</p>
                <div className="flex justify-center gap-8 mt-6">
                  <div>
                    <p className="text-2xl font-heading text-[#00F0FF]">{result.calories}</p>
                    <p className="text-zinc-500 text-xs">calories from protein</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-[#00F0FF]">{result.meals}</p>
                    <p className="text-zinc-500 text-xs">meals per day</p>
                  </div>
                  <div>
                    <p className="text-2xl font-heading text-[#00F0FF]">{result.perMeal}g</p>
                    <p className="text-zinc-500 text-xs">per meal avg</p>
                  </div>
                </div>
              </div>

              {/* Email my protocol — Resend gate (headline number stays visible above) */}
              <ProteinProtocolGate
                weightLb={Math.round(result.weightKg * 2.20462)}
                goalLabel={GOALS.find(g => g.value === goal)?.label || goal}
                dailyProteinG={result.dailyGrams}
                perMealG={result.perMeal}
                mealsPerDay={result.meals}
              />

              {/* Meal Timing */}
              <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8" data-testid="meal-timing-section">
                <h3 className="text-white text-xl font-heading mb-1 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#E63946]" />
                  Meal Timing Breakdown
                </h3>
                <p className="text-zinc-500 text-sm mb-6">Optimal protein distribution across {result.meals} meals</p>
                <div className="space-y-3">
                  {result.timing.map((t, i) => (
                    <div key={t.time} className="flex items-center gap-4 p-4 bg-zinc-800/50 border border-zinc-700/50 hover:border-[#E63946]/30 transition-colors" data-testid={`meal-timing-${i}`}>
                      <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/20 text-[#E63946] font-heading text-sm flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-semibold text-sm">{t.meal}</span>
                          <span className="text-zinc-600 text-xs">{t.time}</span>
                        </div>
                        <p className="text-zinc-500 text-xs">{t.example}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-white font-heading text-lg">{t.grams}g</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-zinc-900/80 border border-zinc-800 p-6">
                <h3 className="text-white text-lg font-heading mb-4 flex items-center gap-2">
                  <Utensils className="w-5 h-5 text-[#00F0FF]" />
                  Pro Tips
                </h3>
                <ul className="space-y-2 text-zinc-400 text-sm">
                  <li className="flex items-start gap-2"><span className="text-[#E63946] mt-0.5">&#8594;</span> Consume 20-40g protein within 2 hours post-workout for optimal muscle protein synthesis</li>
                  <li className="flex items-start gap-2"><span className="text-[#E63946] mt-0.5">&#8594;</span> Distribute protein evenly across meals — avoid loading it all into one sitting</li>
                  <li className="flex items-start gap-2"><span className="text-[#E63946] mt-0.5">&#8594;</span> Leucine-rich sources (whey, eggs, chicken) are most effective for triggering muscle growth</li>
                  <li className="flex items-start gap-2"><span className="text-[#E63946] mt-0.5">&#8594;</span> Casein protein before bed supports overnight recovery</li>
                </ul>
              </div>

              <div className="text-center">
                <Link to="/blogs/article/the-ultimate-guide-to-high-protein-meal-prep" className="text-[#00F0FF] text-sm hover:text-white transition-colors">
                  Read: The Ultimate Guide to High-Protein Meal Prep &#8594;
                </Link>
              </div>

              {/* Save to Dashboard */}
              <SaveResultButton
                saved={saved}
                onSave={() => {
                  saveResult('protein', {
                    value: result.dailyGrams,
                    label: `${result.dailyGrams}g protein/day`,
                    weight: parseFloat(weight),
                    unit,
                    goal: GOALS.find(g => g.value === goal)?.label,
                    activity: ACTIVITY_LEVELS.find(a => a.value === activity)?.label,
                    perMeal: result.perMeal,
                    meals: result.meals,
                    calories: result.calories,
                  });
                  setSaved(true);
                }}
              />
            </div>
          )}
        </div>
      </section>
      <ProteinGuideContent />
    </main>
    </ToolSEOWrapper>
  );
}
